import { Box, Stack } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { Common, Custom, Page, Workflow } from "components";
import { Utils, useFlow } from "components/workflow";
import {
  createDefaultFeedback,
  createDefaultPotluck,
  createDefaultSettings,
  createDefaultUser,
  IConfiguration,
  IPotluck,
  IUser,
} from "interfaces";
import { useApp, useData, useService, useSession, useTheme } from "contexts";
import { ChevronLeftRounded } from "@mui/icons-material";
import { MessageType, SessionType } from "types";
import { Enums, Routes } from "utils";
import { Auxiliars } from "helpers";

const Potluck = () => {
  const { theme } = useTheme();
  const { login } = useSession();
  const { setFeedback, t } = useApp();
  const { emailService, feedbackService, potluckService, userService } = useService();

  const onSubmit = async (name: string, data: Workflow.Utils.PotluckType) => {
    const { user, potluck } = data;

    if (user.role === "guest") {
      const userResponse = await userService.insert({
        ...createDefaultUser(),
        _id: user._id,
        email: user.email,
        first_name: user.first_name,
      });

      if (!userResponse) {
        return setFeedback({
          message: t.message.feedback.request_error,
          severity: Enums.EnumFeedback.Error,
        } as MessageType);
      }

      login({
        id: userResponse._id,
        name: userResponse.full_name,
        email: userResponse.email,
        role: userResponse.role,
        settings: userResponse.settings,
      } as SessionType);
    }
    // else if (user.role === "user") {
    //   login({
    //     id: user._id,
    //     name: user.full_name,
    //     email: user.email,
    //     role: user.role,
    //     settings: createDefaultSettings(),
    //   } as SessionType);
    // }

    if (name === Enums.EnumFlow.Potluck) {
      // TODO : move somewhere else
      potluck.menu = potluck.menu
        .map((menu) => ({
          ...menu,
          categories: menu.categories.filter((cat) => cat.name.length > 0),
        }))
        .filter((menu) => menu.count >= 1);

      const updatedPotluck = Auxiliars.walkAndReplace<IPotluck>(potluck, [
        {
          path: "menu.configuration",
          value: (conf: IConfiguration) => conf._id,
        },
        {
          path: "menu.categories.suggested_by",
          value: user._id,
        },
        {
          path: "menu.categories.assigned_to",
          value: user._id,
        },
        {
          path: "participants",
          value: (users: string[]) =>
            users.includes(user._id) ? users : users.concat(user._id),
        },
        {
          path: "created_by",
          value: user._id,
        },
      ]);

      const response = await potluckService.insert(updatedPotluck);

      if (response) {
        // send email
        // emailService.newPotluck(
        //   user.email,
        //   t.message.subject.new_potluck,
        //   response
        // );

        // save feedback
        feedbackService.insert(data.feedback);

        return setFeedback({
          message: t.message.feedback.request_create_success,
          severity: Enums.EnumFeedback.Success,
        } as MessageType);
      } else {
        return setFeedback({
          message: t.message.feedback.request_error,
          severity: Enums.EnumFeedback.Error,
        } as MessageType);
      }
    }
  };

  const {
    current,
    data,
    direction,
    getVariants,
    handlePrev,
    handleSkip,
    handleSubmit,
    step,
    StepComponent,
    steps,
    stepRef,
    updateData,
  } = useFlow<Workflow.Utils.PotluckType>(
    {
      name: Enums.EnumFlow.Potluck,
      count: Utils.PotluckSteps.length,
      step: 1,
      end: Utils.PotluckSteps.length - 1,
      route: Routes.userdashboard,
    },
    onSubmit
  );

  if (!current) return <Page.Loader />;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        border: 1,
        overflow: "hidden",
        mt: theme.spacing.md,
        px: theme.spacing.lg,
        pt: theme.spacing.lg,
        pb: theme.spacing.xxl,
        borderRadius: theme.border.radius,
        borderColor: theme.color.primary.accent,
      }}
    >
      <AnimatePresence mode="wait">
        <Box
          component={motion.div}
          key={step}
          variants={getVariants(direction)}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.4 }}
        >
          <StepComponent.Component
            ref={stepRef}
            data={data}
            updateData={updateData}
          />
        </Box>
      </AnimatePresence>

      <Box mt={theme.spacing.sm}>
        <Common.Divider sx={{ backgroundColor: theme.color.border.accent }} />
        {StepComponent.skippable && (
          <Box my={theme.spacing.md}>
            <Custom.Button
              fullWidth
              variant="outlined"
              trailing="right"
              onClick={handleSkip}
            >
              {t.action.skip_continue}
            </Custom.Button>
          </Box>
        )}
        <Stack direction="row" spacing={theme.spacing.xs}>
          {step !== steps && (
            <>
              <Custom.Button onClick={handlePrev} secondary>
                <ChevronLeftRounded />
              </Custom.Button>
              <Custom.Button
                submit
                fullWidth
                trailing="right"
                variant="contained"
              >
                {t.action.next}
              </Custom.Button>
            </>
          )}
          {step === steps && (
            <Custom.Button
              submit
              fullWidth
              trailing="right"
              variant="contained"
            >
              {t.action.finish}
            </Custom.Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export const Wrapper = () => {
  const { isAuthenticated, user } = useSession();

  const initUser = isAuthenticated
    ? ({
        _id: user.id,
        first_name: user.name,
        email: user.email,
        role: user.role,
      } as IUser)
    : createDefaultUser();

  return (
    <Workflow.Wrapper
      initData={{
        potluck: createDefaultPotluck(),
        user: initUser,
        feedback: createDefaultFeedback(),
      }}
    >
      <Potluck />
    </Workflow.Wrapper>
  );
};
